import {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Container from '../../components/common/layout/Container';
import {Button, ButtonWithLoader} from '../common/Button';
import ActionButtons from '../common/layout/ActionButtons';
import {addGroup} from '../../api/groups';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import routes from './routes';
import {addGroupValidationSchema} from '../../constants/validationSchemes';
import {CreateGroupProps} from '../../constants/types';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {TextField} from '../common/formik/fields';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const initialValues = {
  slug: '',
  name: ''
};

const AddGroup = () => {
  const {t} = useTranslation('groups');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string[]>>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const onSubmit = async (values: CreateGroupProps) => {
    const sanitizeValues = sanitize(values);
    setApiValidationError(null);

    try {
      await addGroup(sanitizeValues);
      history.push(routes.groups());
      addSuccessToast(t('toasts.createNewGroupMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'group');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const cancelCreation = () => {
    history.push(routes.groups());
  };

  return (
    <Container header="System Configuration - Groups">
      <h2 className="subheader">Create New Group</h2>

      <Formik
        initialValues={initialValues}
        validationSchema={addGroupValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm,
          isSubmitting, isValid, dirty}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <div className="creation-form-wrapper">
                <Form className="form">
                  <FormGroup>
                    <TextField
                      label="Group ID"
                      id="groupId"
                      name="slug"
                      required
                      onFocus={() => {
                        setApiValidationError(null);
                      }}
                      onBlur={() => {
                        setFieldTouched('slug', true);
                      }}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      label="Group Name"
                      id="groupName"
                      name="name"
                      required
                      onFocus={() => {
                        setApiValidationError(null);
                      }}
                      onBlur={() => {
                        setFieldTouched('name', true);
                      }}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>
                </Form>
              </div>

              <ActionButtons>
                <EnsurePermission
                  elementPermissions={permissionsInfo.groups.create}>
                  <ButtonWithLoader
                    type="submit"
                    testId="create-group"
                    disabled={!isValid}
                    isSubmitting={isSubmitting}
                    label="Create new group"
                    onClick={submitForm}
                  />
                </EnsurePermission>

                <Button
                  onClick={cancelCreation}
                  label="Cancel"
                  variant="outlined"
                  color="secondary"
                />
              </ActionButtons>
            </>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddGroup;
